import { useState, type ReactElement } from 'react';
import { CircularProgress } from '@mui/material';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import type { BillingCycle, SubscriptionPricing } from '../../../../hooks/useSubscription';

import { UpgradeModalLayout } from './UpgradeModalLayout';
import { UpgradeForm } from './UpgradeForm';
import { BenefitsPanel } from './BenefitsPanel';
import { SuccessContent } from './SuccessContent';
import { SuccessPanel } from './SuccessPanel';

type ModalView = 'upgrade' | 'success';

export interface UpgradeData {
  billing: BillingCycle;
  goLiveDate?: Date;
}

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: UpgradeData) => Promise<unknown>;
  onComplete?: () => void;
  isAdmin?: boolean;
  pricing?: SubscriptionPricing;
}

export function UpgradeModal({
  open,
  onOpenChange,
  onSubmit,
  onComplete,
  isAdmin = false,
  pricing,
}: UpgradeModalProps): ReactElement {
  const [view, setView] = useState<ModalView>('upgrade');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpgrade = async (data: UpgradeData) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } catch {
      // The mutation's global error handler surfaces the failure; keep the form open to retry.
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    setView('success');
  };

  const handleDone = () => {
    setView('upgrade');
    onComplete?.();
  };

  const handleClose = () => {
    setView('upgrade');
    onOpenChange(false);
  };

  let leftContent: ReactElement;
  if (view === 'success') {
    leftContent = <SuccessContent onDone={handleDone} />;
  } else if (!pricing) {
    leftContent = (
      <div className="vtwo:flex vtwo:justify-center vtwo:py-24">
        <CircularProgress size={24} />
      </div>
    );
  } else {
    leftContent = (
      <UpgradeForm
        isAdmin={isAdmin}
        isSubmitting={isSubmitting}
        pricing={pricing}
        onUpgrade={handleUpgrade}
      />
    );
  }

  const rightContent = view === 'success' ? <SuccessPanel /> : <BenefitsPanel />;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        showCloseButton={false}
        className="vtwo:sm:max-w-[90vw] vtwo:w-[960px] vtwo:p-0 vtwo:gap-0 vtwo:overflow-hidden"
      >
        <UpgradeModalLayout
          left={leftContent}
          right={rightContent}
          onClose={handleClose}
          showClose={view !== 'success'}
        />
      </DialogContent>
    </Dialog>
  );
}
